const clean = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const unique = (values) => [...new Set(values.filter(Boolean).map((v) => String(v).trim()).filter(Boolean))];

class VccRollingSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.querySelectorAll('[data-vcc-track]').forEach((track)=>{[...track.children].forEach((child)=>{if(!child.hasAttribute('data-vcc-group'))child.remove();});track.style.transform='';});
    this.querySelectorAll('[data-vcc-group]').forEach((group)=>group.replaceChildren());
    this.hidden = false;
    this.dataset.ready='false';
    this.abort = new AbortController(); this.rows = []; this.paused = false; this.visible = true; this.tabVisible = !document.hidden;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.bind(); this.initialize();
  }
  disconnectedCallback() { this.abort?.abort(); cancelAnimationFrame(this.frame); this.observer?.disconnect(); delete this.dataset.initialized; }
  bind() {
    const signal=this.abort.signal;
    this.querySelector('[data-vcc-toggle]')?.addEventListener('click',()=>{this.paused=!this.paused;this.updateToggle();},{signal});
    if (this.dataset.pauseHover==='true') { this.addEventListener('pointerenter',(e)=>{if(e.pointerType!=='touch')this.hovered=true;},{signal}); this.addEventListener('pointerleave',()=>{this.hovered=false;this.hoverResumeAt=performance.now()+Number(this.dataset.resumeDelay);},{signal}); }
    document.addEventListener('visibilitychange',()=>this.tabVisible=!document.hidden,{signal});
    this.observer=new IntersectionObserver(([entry])=>this.visible=entry.isIntersecting,{rootMargin:'200px'});this.observer.observe(this);
  }
  searchMode() {
    const mode = this.dataset.searchMode || document.documentElement.dataset.vccSearchMode || 'legacy';
    return ['v4_metafield', 'legacy', 'native_shopify'].includes(mode) ? mode : 'legacy';
  }
  waitForEngine(eventName, getEngine, timeoutMs) {
    const readyEngine = getEngine();
    if (readyEngine) return Promise.resolve(readyEngine);
    return new Promise((resolve) => {
      let settled = false;
      let timer = 0;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(getEngine() || null);
      };
      document.addEventListener(eventName, finish, { once: true, signal: this.abort.signal });
      this.abort.signal.addEventListener('abort', finish, { once: true });
      timer = window.setTimeout(finish, timeoutMs);
    });
  }
  engineForMode(mode) {
    if (mode === 'v4_metafield') {
      return this.waitForEngine('vcc:search-v4-ready', () => window.VCCSearchV4, 3000);
    }
    if (mode === 'legacy') {
      return this.waitForEngine('vcc:smart-search-ready', () => window.VCCSmartSearch, 1500);
    }
    return Promise.resolve(null);
  }
  tiers(analysis) {
    const title=this.dataset.productTitle; const exact=clean(title); const type=analysis.types?.[0]?.label||''; const brand=analysis.brands?.[0]?.label||''; const theme=analysis.themes?.[0]?.label||'';
    const tiers=[];
    if (analysis.exactSources?.length) tiers.push(...analysis.exactSources.map((source)=>source.query));
    if (analysis.team) {
      /* Keep the named subject ahead of item type. A Mighty Ducks hat should
         discover Mighty Ducks merchandise before a generic Starter-hat pool. */
      const aliases=unique(analysis.team.aliases||[]);
      const subject=aliases[0]||analysis.team.label;
      const city=analysis.team.city||String(analysis.team.label).split(' ').slice(0,-1).join(' ');
      const secondaryAliases=aliases.filter((alias)=>clean(alias)!==clean(subject)&&clean(alias)!==clean(analysis.team.label));
      const league=analysis.team.league||'';
      tiers.push(subject,analysis.team.label,city,...secondaryAliases);
      if(type)tiers.push(`${subject} ${type}`,`${analysis.team.label} ${type}`);
      if(brand&&league)tiers.push(`${brand} ${league}`);
      if(league)tiers.push(league);
      tiers.push(type);
    } else {
      const principal=unique(clean(title).split(' ').filter((w)=>w.length>2&&!/^(vintage|retro|classic|rare|authentic|original|mens|womens|unisex|shirt|tee|tshirt|jacket|sweatshirt|hoodie|hat|cap|size|the|and|with|from|1990s|1980s|1970s|2000s)$/.test(w)&&!/^(19|20)\d\d$/.test(w))).slice(0,5).join(' ');
      tiers.push(principal,principal&&type?`${principal} ${type}`:'',brand&&type?`${brand} ${type}`:'',theme&&type?`${theme} ${type}`:'',brand,theme,type);
    }
    if (this.dataset.broaden==='true') tiers.push(...(analysis.searches||[]).map((s)=>s.query));
    return unique(tiers).filter((q)=>clean(q)!==exact).slice(0,10);
  }
  v4Sources(analysis) {
    const exactSources = Array.isArray(analysis?.exactSources) ? analysis.exactSources : [];
    const broadSources = this.dataset.broaden === 'true' && Array.isArray(analysis?.broadSources)
      ? analysis.broadSources
      : [];
    const seen = new Set();
    return exactSources.concat(broadSources).filter((source) => {
      const filters = unique(Array.isArray(source?.v4Filters) ? source.v4Filters : []);
      const key = source?.sourcePlanKey || filters.join('+');
      if (source?.v4Metafield !== true || !filters.length || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  v4SourceGroups(analysis) {
    const groups = [];
    const byKey = new Map();
    for (const source of this.v4Sources(analysis)) {
      const entityPhase = source.entityBlockTarget && source.entityBlockPhase
        ? `entity-block:${source.entityBlockPhase}`
        : '';
      const key = entityPhase || (
        source.exact === true
          ? `exact:${Number(source.tierOrder) || 0}`
          : (source.relationshipGroupId || `tier:${Number(source.tierOrder) || 0}:${source.kind || ''}`)
      );
      if (!byKey.has(key)) {
        const group = { key, sources: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).sources.push(source);
    }
    return groups;
  }
  productIndexValues() {
    const script = this.querySelector('script[data-vcc-product-search-index]');
    let parsed = null;
    try {
      parsed = JSON.parse(script?.textContent || 'null');
    } catch (_error) {}
    return this.normalizeV4IndexInput(parsed);
  }
  normalizeV4IndexInput(input) {
    const flatten = (value) => {
      if (Array.isArray(value)) return value.flatMap(flatten);
      const text = String(value == null ? '' : value).trim();
      if (!text) return [];
      if (text.startsWith('[')) {
        try {
          const nested = JSON.parse(text);
          if (Array.isArray(nested)) return nested.flatMap(flatten);
        } catch (_error) {}
      }
      return text.split(';');
    };
    return unique(flatten(input).map((value) => String(value || '').trim().toLowerCase()));
  }
  productKeys(product) {
    return [
      String(product?.id || '').trim(),
      clean(product?.handle),
      clean(String(product?.url || '').split('?')[0])
    ].filter(Boolean);
  }
  async collectLegacy(engine, analysis, needed) {
    const products = [];
    const counts = [];
    const seen = new Set(this.productKeys({
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      url: this.dataset.productUrl
    }));
    for (const query of this.tiers(analysis)) {
      if (products.length >= needed) break;
      const before = products.length;
      for (let page = 1; page <= 3 && products.length < needed; page += 1) {
        const result = await this.fetchLegacyTier(query, page);
        for (const product of result.products) {
          const keys = this.productKeys(product);
          if (keys.some((key) => seen.has(key))) continue;
          if (this.dataset.includeSoldout !== 'true' && !product.available) continue;
          keys.forEach((key) => seen.add(key));
          product.rank = products.length;
          product.score = engine.productScore?.(product, analysis, {
            weight: 100 - counts.length * 7,
            kind: counts.length < 5 ? 'best' : 'broad'
          }) || 0;
          products.push(product);
          if (products.length >= needed) break;
        }
        if (!result.hasNext) break;
      }
      counts.push(`${query}: ${products.length - before}`);
    }
    products.sort((left, right) => (right.score - left.score) || (left.rank - right.rank));
    return { products, counts };
  }
  async collectV4SourceProducts(engine, analysis, source, seen, target) {
    const sourceProducts = [];
    const sourceSeen = new Set();
    const pageLimit = Math.max(1, Math.min(3, Number(source.maxSourcePages) || 1));
    const sourceTarget = Math.max(1, Number(target) || 1);
    for (let page = 1; page <= pageLimit; page += 1) {
      const result = await this.fetchV4Source(source, page);
      for (const product of result.products) {
        const keys = this.productKeys(product);
        if (keys.some((key) => seen.has(key) || sourceSeen.has(key))) continue;
        if (this.dataset.includeSoldout !== 'true' && !product.available) continue;
        keys.forEach((key) => sourceSeen.add(key));
        product.rank = sourceProducts.length;
        product.score = engine.productScore?.(product, analysis, source) || 0;
        sourceProducts.push(product);
      }
      if (sourceProducts.length >= sourceTarget || !result.hasNext) break;
    }
    sourceProducts.sort((left, right) => (right.score - left.score) || (left.rank - right.rank));
    return sourceProducts;
  }
  async collectV4(engine, analysis, needed) {
    const products = [];
    const counts = [];
    const seen = new Set(this.productKeys({
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      url: this.dataset.productUrl
    }));
    const sourceGroups = this.v4SourceGroups(analysis);
    const sources = sourceGroups.flatMap((group) => group.sources);
    this.dataset.v4SourceCount = String(sources.length);
    if (analysis?.nativeFallback === true || analysis?.recognized !== true || !sources.length) {
      return { products, counts };
    }

    for (let groupIndex = 0; groupIndex < sourceGroups.length; groupIndex += 1) {
      if (products.length >= needed) break;
      const sourceGroup = sourceGroups[groupIndex];
      const memberProducts = [];
      const remaining = Math.max(1, needed - products.length);
      const batchWidth = Math.min(4, sourceGroup.sources.length);
      for (let sourceIndex = 0; sourceIndex < sourceGroup.sources.length; sourceIndex += batchWidth) {
        const batch = sourceGroup.sources.slice(sourceIndex, sourceIndex + batchWidth);
        const batchProducts = await Promise.all(batch.map((source) => {
          return this.collectV4SourceProducts(engine, analysis, source, seen, remaining);
        }));
        memberProducts.push(...batchProducts);

        const candidateSeen = new Set(seen);
        let candidateCount = 0;
        for (const candidates of memberProducts) {
          for (const candidate of candidates) {
            const keys = this.productKeys(candidate);
            if (keys.some((key) => candidateSeen.has(key))) continue;
            keys.forEach((key) => candidateSeen.add(key));
            candidateCount += 1;
          }
        }
        if (candidateCount >= remaining) break;
      }

      // Members of one relationship tier are peers. Merge them round-robin so
      // a large first team/route cannot consume the rail before its siblings.
      const before = products.length;
      const positions = memberProducts.map(() => 0);
      while (products.length < needed) {
        let progressed = false;
        for (let memberIndex = 0; memberIndex < memberProducts.length; memberIndex += 1) {
          const candidates = memberProducts[memberIndex];
          let product = null;
          while (positions[memberIndex] < candidates.length && !product) {
            const candidate = candidates[positions[memberIndex]++];
            const keys = this.productKeys(candidate);
            if (!keys.some((key) => seen.has(key))) product = candidate;
          }
          if (!product) continue;
          this.productKeys(product).forEach((key) => seen.add(key));
          products.push(product);
          progressed = true;
          if (products.length >= needed) break;
        }
        if (!progressed) break;
      }
      const label = sourceGroup.sources[0]?.label || sourceGroup.sources[0]?.kind || `Tier ${groupIndex + 1}`;
      counts.push(`${label}: ${products.length - before}`);
    }
    return { products, counts };
  }
  async initialize() {
    const mode = this.searchMode();
    this.dataset.engineMode = mode;
    this.dataset.engineExclusive = 'true';
    this.requestCount = 0;
    if (mode === 'native_shopify') {
      this.dataset.state = 'native-disabled';
      this.hidden = true;
      return;
    }
    try {
      const engine = await this.engineForMode(mode);
      if (!engine) throw new Error(`${mode} search engine is unavailable.`);
      const analysis = mode === 'v4_metafield'
        ? (
          engine.analyzeProductIndexAsync
            ? await engine.analyzeProductIndexAsync(this.productIndexValues(), this.dataset.productTitle)
            : engine.analyzeProductIndex(this.productIndexValues(), this.dataset.productTitle)
        )
        : (
          engine.analyzeSearchAsync
            ? await engine.analyzeSearchAsync(this.dataset.productTitle)
            : engine.analyzeSearch(this.dataset.productTitle)
        );
      const needed = Number(this.dataset.rows) * Number(this.dataset.perRow);
      const result = mode === 'v4_metafield'
        ? await this.collectV4(engine, analysis, needed)
        : await this.collectLegacy(engine, analysis, needed);
      if (result.products.length < Number(this.dataset.minimum)) {
        this.dataset.state = 'insufficient';
        this.hidden = true;
        return;
      }
      this.distribute(result.products);
      this.dataset.ready = 'true';
      this.dataset.state = 'ready';
      this.dataset.resultCount = String(result.products.length);
      this.dataset.routeTemplate = String(analysis?.routeTemplate || '');
      this.querySelector('[data-vcc-status]').textContent = `${result.products.length} ranked products`;
      const debug = this.querySelector('[data-vcc-debug]');
      if (debug) {
        debug.textContent = `Query tiers\n${result.counts.join('\n')}\n\nCurrent product excluded; ${result.products.length} unique storefront results.`;
      }
      requestAnimationFrame(() => this.prepareTracks());
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.dataset.ready = 'false';
      this.dataset.state = 'unavailable';
      this.hidden = true;
      console.error('[VCC rolling search]', error);
    }
  }
  searchTransportUrl(page) {
    const url = new URL(this.dataset.searchUrl, location.origin);
    url.searchParams.set('section_id', 'vcc-search-results');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'hide');
    url.searchParams.set('page', String(page));
    return url;
  }
  async fetchDocument(url, retry = 0) {
    this.requestCount += 1;
    this.dataset.requestCount = String(this.requestCount);
    const response = await fetch(url, { credentials: 'same-origin', signal: this.abort.signal });
    if (response.status === 429 && retry < 1) {
      const retryAfter = Math.max(500, Math.min(2500, (Number(response.headers.get('Retry-After')) || 1) * 1000));
      await new Promise((resolve) => window.setTimeout(resolve, retryAfter));
      return this.fetchDocument(url, retry + 1);
    }
    if (!response.ok) throw new Error(`Search ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }
  productsFromDocument(doc) {
    return [...doc.querySelectorAll('.vcc-search-hit')].map((el) => ({
      id: el.dataset.id,
      handle: el.dataset.handle,
      url: el.dataset.url,
      title: el.dataset.title,
      vendor: el.dataset.vendor,
      type: el.dataset.type || el.dataset.productType,
      product_type: el.dataset.productType || el.dataset.type,
      tags: el.dataset.tags || '',
      price: el.dataset.price,
      priceRaw: el.dataset.priceRaw,
      price_min: el.dataset.priceRaw,
      available: el.dataset.available === 'true',
      variant_id: el.dataset.variantId,
      first_available_variant_id: el.dataset.firstAvailableVariantId || el.dataset.variantId,
      image: el.dataset.image,
      srcset: [
        el.dataset.image360 && `${el.dataset.image360} 360w`,
        el.dataset.image540 && `${el.dataset.image540} 540w`,
        el.dataset.image720 && `${el.dataset.image720} 720w`,
        el.dataset.image900 && `${el.dataset.image900} 900w`
      ].filter(Boolean).join(', '),
      width: el.dataset.width,
      height: el.dataset.height,
      alt: el.dataset.alt
    }));
  }
  v4SearchIndexValues(script) {
    let parsed = null;
    try {
      parsed = JSON.parse(script?.textContent || 'null');
    } catch (_error) {}
    return this.normalizeV4IndexInput(parsed);
  }
  v4FilterResponseAttestation(doc, source) {
    const transport = doc.querySelector('.vcc-search-transport');
    const filterAvailable = transport?.dataset.vccIndexFilterAvailable === 'true';
    const filterActive = transport?.dataset.vccIndexFilterActive === 'true';
    const requestedFilters = unique(Array.isArray(source?.v4Filters) ? source.v4Filters : []);
    const indexes = new Map([...doc.querySelectorAll('script[data-vcc-search-index]')].map((script) => [
      String(script.getAttribute('data-vcc-search-index') || ''),
      new Set(this.v4SearchIndexValues(script))
    ]));
    const hits = [...doc.querySelectorAll('.vcc-search-hit')];
    const productsAttested = hits.length === 0 || hits.every((hit) => {
      const id = String(hit.getAttribute?.('data-id') || hit.dataset?.id || '');
      const values = indexes.get(id);
      return values instanceof Set && requestedFilters.every((filter) => values.has(filter));
    });
    const filterApplied = filterAvailable
      && requestedFilters.length > 0
      && productsAttested;
    return {
      filterAvailable,
      filterApplied,
      shopifyActive: filterActive,
      attestation: filterApplied ? (hits.length ? 'product-index' : 'verified-empty') : 'failed'
    };
  }
  async fetchLegacyTier(query, page) {
    const url = this.searchTransportUrl(page);
    url.searchParams.set('q', query);
    const doc = await this.fetchDocument(url);
    return {
      hasNext: doc.querySelector('[data-has-next="true"]') !== null,
      products: this.productsFromDocument(doc)
    };
  }
  async fetchV4Source(source, page) {
    const url = this.searchTransportUrl(page);
    url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('q', this.dataset.v4NeutralQuery || '-tag:__vcc_never__');
    const parameter = source.filterParameter || this.dataset.v4FilterParameter || 'filter.p.m.vcc_search.index';
    unique(Array.isArray(source.v4Filters) ? source.v4Filters : []).forEach((value) => {
      url.searchParams.append(parameter, value);
    });
    const doc = await this.fetchDocument(url);
    const transport = doc.querySelector('.vcc-search-transport');
    const attestation = this.v4FilterResponseAttestation(doc, source);
    this.dataset.v4FilterAvailable = String(attestation.filterAvailable);
    this.dataset.v4FilterApplied = String(attestation.filterApplied);
    this.dataset.v4FilterActive = String(attestation.shopifyActive);
    this.dataset.v4FilterAttestation = attestation.attestation;
    if (!attestation.filterApplied) {
      throw new Error('V4 metafield filter is unavailable or its response could not be attested.');
    }
    return {
      hasNext: transport?.dataset.hasNext === 'true',
      products: this.productsFromDocument(doc)
    };
  }
  distribute(products) {
    const rows=Number(this.dataset.rows), count=Number(this.dataset.perRow); let lists=[];
    if(rows===1)lists=[products.slice(0,count)];else if(this.dataset.distribution==='alternate'){lists=[products.filter((_,i)=>i%2===0).slice(0,count),products.filter((_,i)=>i%2===1).slice(0,count)];}else if(this.dataset.distribution==='even'){const half=Math.ceil(Math.min(products.length,count*2)/2);lists=[products.slice(0,half),products.slice(half,half+count)];}else{lists=[products.slice(0,count),products.slice(count,count*2)];}
    lists.forEach((list,index)=>{if(!list.length)return;const row=this.querySelector(`[data-vcc-row="${index}"]`);row.hidden=false;const group=row.querySelector('[data-vcc-group]');group.innerHTML=list.map((p,i)=>this.card(p,i)).join('');});
  }
  card(p,index) { const info=[this.dataset.showVendor==='true'?`<p class="vcc-rolling-card__vendor">${this.escape(p.vendor)}</p>`:'',this.dataset.showTitle==='true'?`<p class="vcc-rolling-card__title">${this.escape(p.title)}</p>`:'',this.dataset.showPrice==='true'?`<p class="vcc-rolling-card__price">${this.escape(p.price)}</p>`:'',this.dataset.showCta==='true'?`<span class="vcc-rolling-card__cta">${this.escape(this.dataset.cta)}</span>`:''].join('');return `<a class="vcc-rolling-card" href="${this.escape(p.url)}" aria-label="View ${this.escape(p.title)}" draggable="false"><span class="vcc-rolling-card__image-wrap">${p.image?`<img class="vcc-rolling-card__image" src="${this.escape(p.image)}" srcset="${this.escape(p.srcset)}" sizes="(max-width:749px) ${this.dataset.cardMobile}px, ${this.dataset.cardDesktop}px" width="${p.width||900}" height="${p.height||900}" alt="${this.escape(p.alt||p.title)}" loading="${index<4?'eager':'lazy'}" draggable="false">`:''}</span>${info?`<span class="vcc-rolling-card__overlay">${info}</span>`:''}</a>`; }
  escape(value){const d=document.createElement('div');d.textContent=value||'';return d.innerHTML.replace(/"/g,'&quot;');}
  prepareTracks() { this.querySelectorAll('[data-vcc-row]:not([hidden])').forEach((row,index)=>{const group=row.querySelector('[data-vcc-group]');const originals=[...group.children].map((n)=>n.cloneNode(true));while(group.scrollWidth<innerWidth*1.35&&originals.length)originals.forEach((n)=>group.append(n.cloneNode(true)));const clone=group.cloneNode(true);clone.removeAttribute('data-vcc-group');clone.setAttribute('aria-hidden','true');clone.querySelectorAll('a').forEach((a)=>a.tabIndex=-1);row.querySelector('[data-vcc-track]').append(clone);const state={row,track:row.querySelector('[data-vcc-track]'),group,width:group.getBoundingClientRect().width,x:0,last:performance.now(),direction:row.dataset.direction==='left'?-1:1,speed:Number(row.dataset.speed),drag:false,resumeAt:0};this.bindDrag(state);this.rows.push(state);});if(!this.reduced)this.frame=requestAnimationFrame((t)=>this.tick(t)); }
  bindDrag(state){
    const signal=this.abort.signal;let pointerId=null,startX=0,startOffset=0,pending=false;
    state.row.addEventListener('click',(event)=>{if(performance.now()<(state.suppressClickUntil||0)){event.preventDefault();event.stopPropagation();state.suppressClickUntil=0;}},{signal,capture:true});
    state.row.addEventListener('pointerdown',(event)=>{if(event.button!==0)return;pointerId=event.pointerId;pending=true;state.drag=false;startX=event.clientX;startOffset=state.x;},{signal});
    state.row.addEventListener('pointermove',(event)=>{if(!pending||event.pointerId!==pointerId)return;const raw=event.clientX-startX;if(!state.drag&&Math.abs(raw)<7)return;if(!state.drag){state.drag=true;state.row.dataset.dragging='true';try{state.row.setPointerCapture(pointerId);}catch(error){/* Pointer may already have ended. */}}state.x=startOffset+raw*Number(this.dataset.dragSensitivity);this.normalize(state);this.render(state);},{signal});
    const end=(event)=>{if(!pending||event.pointerId!==pointerId)return;pending=false;if(state.drag){state.suppressClickUntil=performance.now()+300;state.resumeAt=performance.now()+Number(this.dataset.resumeDelay);state.drag=false;state.row.dataset.dragging='false';try{if(state.row.hasPointerCapture(pointerId))state.row.releasePointerCapture(pointerId);}catch(error){/* No active capture. */}}pointerId=null;};
    state.row.addEventListener('pointerup',end,{signal});state.row.addEventListener('pointercancel',end,{signal});
  }
  tick(time){for(const s of this.rows){const delta=Math.min(48,time-s.last);s.last=time;const mobile=matchMedia('(max-width:749px)').matches;const autoplay=mobile?this.dataset.mobileAutoplay==='true':this.dataset.desktopAutoplay==='true';if(autoplay&&!this.paused&&!this.hovered&&!s.drag&&time>s.resumeAt&&time>(this.hoverResumeAt||0)&&this.visible&&this.tabVisible){s.x+=s.direction*s.speed*delta/1000;this.normalize(s);this.render(s);}}this.frame=requestAnimationFrame((t)=>this.tick(t));}
  normalize(s){if(!s.width)return;while(s.x<=-s.width)s.x+=s.width;while(s.x>=0)s.x-=s.width;}
  render(s){s.track.style.transform=`translate3d(${s.x}px,0,0)`;}
  updateToggle(){const b=this.querySelector('[data-vcc-toggle]');if(!b)return;b.dataset.paused=String(this.paused);b.setAttribute('aria-pressed',String(this.paused));b.setAttribute('aria-label',this.paused?'Play product movement':'Pause product movement');const label=b.querySelector('[data-vcc-toggle-label]');if(label)label.textContent=this.paused?'Play movement':'Pause movement';}
}
if(!customElements.get('vcc-rolling-search'))customElements.define('vcc-rolling-search',VccRollingSearch);
